import { api, type AdminMember, type AdminOrganisation, type AdminOrganisationDetail, type Standing } from '../api';
import { callout, field, input, openDialog, val } from '../dialog';
import { errorCard, esc, fmtDate, html, loading, pill, tile, toast } from '../ui';

/**
 * The installation, seen from outside every organisation in it.
 *
 * This is the only screen in the application that shows more than one tenant,
 * and it is deliberately plain: a list, a panel, and buttons that each do one
 * legible thing. Nothing here is a bulk action. Somebody administering
 * customers at four in the afternoon should have to mean each thing they do,
 * and every one of these writes a row into the affected organisation's own
 * audit log — being administered from outside is exactly what a record is for.
 */

const TONE: Record<Standing['state'], 'OK' | 'DUE_SOON' | 'OVERDUE' | 'NA'> = {
  ACTIVE: 'OK',
  TRIAL: 'DUE_SOON',
  AWAITING_VERIFICATION: 'DUE_SOON',
  EXPIRED: 'OVERDUE',
  SUSPENDED: 'OVERDUE',
};

const LABEL: Record<Standing['state'], string> = {
  ACTIVE: 'Active',
  TRIAL: 'On trial',
  AWAITING_VERIFICATION: 'Not verified',
  EXPIRED: 'Trial ended',
  SUSPENDED: 'Suspended',
};

const standingPill = (s: Standing) =>
  pill(TONE[s.state], s.state === 'TRIAL' && s.daysLeft !== null ? `On trial · ${s.daysLeft}d` : LABEL[s.state]);

export async function renderConsole(host: HTMLElement, organisationId?: string): Promise<void> {
  host.innerHTML = loading('Loading the installation');
  try {
    const organisations = await api.console.organisations();
    const detail = organisationId ? await api.console.organisation(organisationId) : null;
    host.innerHTML = view(organisations, detail);
    bind(host, organisationId, detail);
  } catch (e) {
    // A flat "Not available" is what somebody who is not us gets, and it is
    // not worth dressing up as anything more informative.
    host.innerHTML = errorCard(e instanceof Error ? e.message : 'Not available.');
  }
}

function view(organisations: AdminOrganisation[], detail: AdminOrganisationDetail | null): string {
  const live = organisations.filter((o) => !o.restoredAt);
  const onTrial = live.filter((o) => o.standing.state === 'TRIAL').length;
  const stopped = live.filter((o) => !o.standing.mayUse).length;

  return html`
    <div class="page-h">
      <div>
        <h1>The installation</h1>
        <p class="dim">
          Every organisation on this server — whoever runs it, not whoever uses it. Each change is
          recorded in that organisation's own audit log.
        </p>
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Organisations', value: live.length, sub: 'not counting restored copies' })}
      ${tile({ label: 'On trial', value: onTrial, sub: 'inside a free period', tone: onTrial ? 'caution' : undefined })}
      ${tile({ label: 'Cannot be used', value: stopped, sub: 'expired, suspended or unverified', tone: stopped ? 'exceed' : 'ok' })}
      ${tile({ label: 'Aircraft', value: live.reduce((a, o) => a + o.aircraftCount, 0), sub: 'across the installation' })}
    </div>

    <div class="card">
      <div class="card-h"><div><h2>Organisations</h2></div></div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>Name</th><th>Approval</th><th>Standing</th><th>Trial ends</th>
              <th class="col-opt">Aircraft</th><th class="col-opt">Logins</th><th></th>
            </tr>
          </thead>
          <tbody>
            ${organisations.map((o) => html`
              <tr${o.restoredAt ? ' style="opacity:.55"' : ''}>
                <td>
                  <b>${esc(o.name)}</b>
                  ${o.restoredAt ? html`<span class="fl fl-ref">copy of a backup</span>` : ''}
                  <div class="tiny dim">${esc(o.email ?? 'no address on file')}</div>
                </td>
                <td class="tiny">${esc(o.approvalReference ?? '—')}<div class="dim">${esc(o.approvalKind)}</div></td>
                <td>${standingPill(o.standing)}</td>
                <td class="tiny">${o.trialEndsAt ? esc(fmtDate(o.trialEndsAt)) : html`<span class="dim">no trial</span>`}</td>
                <td class="col-opt">${o.aircraftCount}</td>
                <td class="col-opt">${o.memberCount}</td>
                <td><button class="btn sm" data-open-org="${esc(o.id)}">Open</button></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>

    ${detail ? organisationPanel(detail) : ''}
  `;
}

function organisationPanel(d: AdminOrganisationDetail): string {
  const o = d.organisation;
  const s = o.standing;

  return html`
    <div class="card" data-org-panel="${esc(o.id)}">
      <div class="card-h">
        <div>
          <h2>${esc(o.name)}</h2>
          <p class="tiny dim">
            Created ${esc(fmtDate(o.createdAt))} ·
            ${o.activatedAt ? `verified ${esc(fmtDate(o.activatedAt))}` : 'address never confirmed'}
          </p>
        </div>
        ${standingPill(s)}
      </div>

      ${s.message ? callout(s.mayUse ? 'info' : 'caution', s.mayUse ? 'i' : '!', esc(s.message)) : ''}

      <div class="rows" style="margin-top:12px">
        <div class="row">
          <div class="bd">
            <div class="t">Trial</div>
            <div class="m">
              ${o.trialEndsAt
                ? `Ends ${esc(fmtDate(o.trialEndsAt))}. Extending adds to what is left rather than starting again.`
                : 'No trial — this organisation has no end date on it.'}
            </div>
          </div>
          <div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end">
            <button class="btn sm" data-extend="30">+30 days</button>
            <button class="btn sm" data-extend="90">+90 days</button>
            ${o.trialEndsAt ? html`<button class="btn sm" data-remove-trial>Remove the limit</button>` : ''}
          </div>
        </div>

        <div class="row">
          <div class="bd">
            <div class="t">${o.suspendedAt ? 'Suspended' : 'In service'}</div>
            <div class="m">
              ${o.suspendedAt
                ? `Turned off ${esc(fmtDate(o.suspendedAt))}${o.suspendedReason ? ` — ${esc(o.suspendedReason)}` : ''}.
                   Lifting it puts the organisation back exactly where it was; the trial is untouched.`
                : 'Suspending stops anybody signing in. Nothing is deleted and the trial keeps its date.'}
            </div>
          </div>
          ${o.suspendedAt
            ? html`<button class="btn sm" data-suspend="off">Put back in service</button>`
            : html`<button class="btn sm danger" data-suspend="on">Suspend</button>`}
        </div>

        ${o.activatedAt
          ? ''
          : html`
              <div class="row">
                <div class="bd">
                  <div class="t">The address has never been confirmed</div>
                  <div class="m">
                    Nobody has followed the link sent when this organisation was created. Confirming it
                    by hand is for the case the mail cannot solve — a mistyped address, or a mail server
                    that drops us silently.
                  </div>
                </div>
                <button class="btn sm" data-activate>Confirm it by hand</button>
              </div>
            `}

        <div class="row">
          <div class="bd">
            <div class="t">Details</div>
            <div class="m">
              ${esc([o.addressLine1, o.city, o.countryCode].filter(Boolean).join(', ') || 'no address on file')}
              ${o.companyRegistration ? ` · ${esc(String(o.companyRegistration))}` : ''}
            </div>
          </div>
          <button class="btn sm" data-edit-org>Edit</button>
        </div>

        <div class="row">
          <div class="bd">
            <div class="t">Their records, as a file</div>
            <div class="m">
              Everything this organisation holds, taken on their behalf. Do this before closing an
              account: the operator is still obliged to hold these records, and after a deletion
              nobody can produce them.
            </div>
          </div>
          <button class="btn sm" data-backup-org>Download</button>
        </div>

        <div class="row">
          <div class="bd">
            <div class="t" style="color:var(--exceed)">Delete this organisation</div>
            <div class="m">
              Permanent, and it takes everything: ${o.aircraftCount} aircraft, ${o.memberCount} login(s),
              every record and document. There is no undo and no copy kept — a backup taken beforehand
              is the only way anything survives.
            </div>
          </div>
          <button class="btn sm danger" data-delete-org>Delete…</button>
        </div>
      </div>

      <div class="card-h" style="margin-top:18px"><div><h2>Logins</h2></div></div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr><th>Name</th><th>Address</th><th>Roles</th><th class="col-opt">Last in</th><th></th></tr>
          </thead>
          <tbody>${d.members.map(memberRow).join('')}</tbody>
        </table>
      </div>
    </div>
  `;
}

function memberRow(m: AdminMember): string {
  const u = m.user;
  const off = Boolean(u.disabledAt) || Boolean(m.revokedAt);

  return html`
    <tr${off ? ' style="opacity:.55"' : ''}>
      <td>
        <b>${esc(`${u.firstName} ${u.lastName}`.trim() || '—')}</b>
        ${u.platformAdmin ? html`<span class="fl fl-ref">platform</span>` : ''}
        ${m.revokedAt ? html`<div class="tiny dim">membership revoked</div>` : ''}
      </td>
      <td class="tiny">
        ${esc(u.email)}
        <div class="dim">
          ${u.emailVerifiedAt ? 'confirmed' : 'not confirmed'} ·
          ${u.hasPassword ? 'password' : 'Google only'}
        </div>
      </td>
      <td class="tiny">${m.roles.map((r) => esc(r)).join(', ')}</td>
      <td class="col-opt tiny">${u.lastLoginAt ? esc(fmtDate(u.lastLoginAt)) : html`<span class="dim">never</span>`}</td>
      <td style="white-space:nowrap;display:flex;gap:5px;justify-content:flex-end">
        <button class="btn sm" data-reset="${esc(u.id)}">Reset password</button>
        <button class="btn sm" data-email="${esc(u.id)}" data-current="${esc(u.email)}">Email</button>
        ${u.disabledAt
          ? html`<button class="btn sm" data-user-enable="${esc(u.id)}">Enable</button>`
          : html`<button class="btn sm danger" data-user-disable="${esc(u.id)}">Disable</button>`}
      </td>
    </tr>
  `;
}

function bind(host: HTMLElement, organisationId?: string, detail?: AdminOrganisationDetail | null): void {
  const reload = (id?: string) => void renderConsole(host, id ?? organisationId);

  host.querySelectorAll<HTMLButtonElement>('[data-open-org]').forEach((b) =>
    b.addEventListener('click', () => reload(b.dataset.openOrg)),
  );

  if (!organisationId) return;
  const id = organisationId;
  // Needed for the deletion confirmation, which compares what was typed
  // against the real name rather than trusting the button that opened it.
  const name = detail?.organisation?.name ?? '';

  /** Every write goes through here, so one place reports and one place reloads. */
  const run = async (button: HTMLButtonElement, what: () => Promise<unknown>, said: string) => {
    const was = button.textContent;
    button.disabled = true;
    button.textContent = 'Working…';
    try {
      await what();
      toast(said);
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not go through.', 'error');
      button.disabled = false;
      button.textContent = was;
    }
  };

  host.querySelectorAll<HTMLButtonElement>('[data-extend]').forEach((b) =>
    b.addEventListener('click', () => {
      const days = Number(b.dataset.extend);
      void run(b, () => api.console.extendTrial(id, days), `Extended by ${days} days`);
    }),
  );

  host.querySelector<HTMLButtonElement>('[data-remove-trial]')?.addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    void run(b, () => api.console.removeTrial(id), 'The time limit is off — this is a customer now');
  });

  host.querySelector<HTMLButtonElement>('[data-activate]')?.addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    void run(b, () => api.console.activate(id), 'Marked as confirmed');
  });

  host.querySelectorAll<HTMLButtonElement>('[data-suspend]').forEach((b) =>
    b.addEventListener('click', () => {
      if (b.dataset.suspend === 'off') {
        void run(b, () => api.console.suspend(id, false), 'Back in service');
        return;
      }
      // A reason, because "why can we not sign in" is the next question and
      // the answer should already be on the screen when it is asked.
      openDialog<{ reason: string }>({
        title: 'Suspend this organisation',
        sub: 'Nobody will be able to sign in to it',
        primary: 'Suspend it',
        body: () => html`
          ${field('Why', input('reason', '', 'maxlength="200" placeholder="Unpaid since March"'),
            'Shown to whoever is turned away, so being locked out is answerable without a phone call')}
          ${callout('caution', '!', html`
            Nothing is deleted and the trial keeps its date. Lifting the suspension puts the
            organisation back exactly where it was.
          `)}
        `,
        collect: (root) => {
          const reason = val(root, 'reason');
          if (!reason) return { error: 'Give a reason — it is what they will be shown.' };
          return { reason };
        },
        submit: (p) => api.console.suspend(id, true, p.reason),
        done: () => 'Suspended',
        onDone: () => reload(),
      });
    }),
  );

  const backupButton = host.querySelector<HTMLButtonElement>('[data-backup-org]');
  backupButton?.addEventListener('click', () => {
    const was = backupButton.textContent;
    backupButton.disabled = true;
    backupButton.textContent = 'Building…';
    void api.console
      .backupOf(id, name)
      .then((file) => toast(`${file} — saved to your downloads`))
      .catch((e: Error) => toast(e.message || 'Could not build that backup', 'error'))
      .finally(() => {
        backupButton.disabled = false;
        backupButton.textContent = was;
      });
  });

  /**
   * Deleting is the only thing here that cannot be undone, so the dialog is
   * built to slow the hand down: the name has to be typed, the backup is one
   * click away inside the same dialog, and the button stays disabled until the
   * name matches. None of that stops a determined mistake — it stops the
   * absent-minded one, which is the mistake that actually happens.
   */
  host.querySelector<HTMLButtonElement>('[data-delete-org]')?.addEventListener('click', () => {
    openDialog<{ confirmName: string }>({
      wide: true,
      title: 'Delete this organisation',
      sub: name,
      primary: 'Delete it permanently',
      body: () => html`
        ${callout('caution', '!', html`
          <b>Everything goes, and nothing is kept.</b> Their aircraft, logbooks, worksheets,
          airworthiness review records, documents, invoices and logins. There is no undo here and no
          copy on our side.
        `)}
        <div class="rows" style="margin:14px 0">
          <div class="row">
            <div class="bd">
              <div class="t">Take their records out first</div>
              <div class="m">
                ML.A.305(h) obliges the operator to hold these for the life of the aircraft plus two
                years. Closing an account does not end that, and after this nobody can produce them.
              </div>
            </div>
            <button type="button" class="btn sm" data-dlg-backup>Download the backup</button>
          </div>
        </div>
        ${field('Type the organisation\'s name to confirm', input('confirmName', '', 'maxlength="200" autocomplete="off"'),
          `Exactly: ${name}`)}
      `,
      onRender: (root) => {
        const button = root.querySelector<HTMLButtonElement>('[data-dlg-backup]');
        button?.addEventListener('click', () => {
          const was = button.textContent;
          button.disabled = true;
          button.textContent = 'Building…';
          void api.console
            .backupOf(id, name)
            .then((file) => toast(`${file} — saved to your downloads`))
            .catch((e: Error) => toast(e.message || 'Could not build that backup', 'error'))
            .finally(() => {
              button.disabled = false;
              button.textContent = was;
            });
        });
      },
      collect: (root) => {
        const typed = val(root, 'confirmName');
        if (typed.trim() !== name.trim()) {
          return { error: 'That is not the name. Type it exactly as it appears above.' };
        }
        return { confirmName: typed };
      },
      submit: (p) => api.console.deleteOrganisation(id, p.confirmName),
      done: (r: { name: string; rows: number }) => `${r.name} deleted — ${r.rows} row(s) removed`,
      // Back to the list: the organisation this panel described no longer exists.
      onDone: () => void renderConsole(host),
    });
  });

  host.querySelector<HTMLButtonElement>('[data-edit-org]')?.addEventListener('click', () => {
    const panel = host.querySelector('[data-org-panel]');
    void editOrganisation(id, panel, reload);
  });

  host.querySelectorAll<HTMLButtonElement>('[data-reset]').forEach((b) =>
    b.addEventListener('click', () =>
      void run(b, async () => {
        const r = await api.console.resetPassword(id, b.dataset.reset!);
        toast(`A reset link is on its way to ${r.sentTo}`);
      }, 'Sent'),
    ),
  );

  host.querySelectorAll<HTMLButtonElement>('[data-user-disable]').forEach((b) =>
    b.addEventListener('click', () =>
      void run(b, () => api.console.setUserDisabled(id, b.dataset.userDisable!, true), 'That login is off'),
    ),
  );

  host.querySelectorAll<HTMLButtonElement>('[data-user-enable]').forEach((b) =>
    b.addEventListener('click', () =>
      void run(b, () => api.console.setUserDisabled(id, b.dataset.userEnable!, false), 'That login works again'),
    ),
  );

  host.querySelectorAll<HTMLButtonElement>('[data-email]').forEach((b) =>
    b.addEventListener('click', () => {
      const userId = b.dataset.email!;
      openDialog<{ email: string }>({
        title: 'Change the address on this login',
        sub: esc(b.dataset.current ?? ''),
        primary: 'Change it',
        body: () => html`
          ${field('New address', input('email', '', 'type="email" maxlength="254"'))}
          ${callout('caution', '!', html`
            This address is the identity behind everything that account has signed. The new one starts
            <b>unconfirmed</b> and a verification link is sent to it. An address that already has an
            account cannot be used — two people's records are not merged from here.
          `)}
        `,
        collect: (root) => {
          const email = val(root, 'email');
          if (!email.includes('@')) return { error: 'That is not an email address.' };
          return { email };
        },
        submit: (p) => api.console.changeEmail(id, userId, p.email),
        done: (r: { email: string }) => `Changed to ${r.email} — a confirmation link has been sent`,
        onDone: () => reload(),
      });
    }),
  );
}

/** The details an organisation would edit itself, reachable when they cannot. */
function editOrganisation(id: string, panel: Element | null, reload: () => void): void {
  const read = (name: string) => panel?.querySelector(`[data-field="${name}"]`)?.textContent ?? '';
  void read;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Organisation details',
    sub: 'The same fields they can edit themselves',
    primary: 'Save',
    body: () => html`
      <div class="frow">
        ${field('Name', input('name', '', 'maxlength="200"'))}
        ${field('Legal name', input('legalName', '', 'maxlength="200"'))}
      </div>
      <div class="frow3">
        ${field('Approval reference', input('approvalReference', '', 'maxlength="40"'))}
        ${field('Authority', input('competentAuthority', '', 'maxlength="80"'))}
        ${field('Country', input('countryCode', '', 'maxlength="2" style="text-transform:uppercase"'))}
      </div>
      <div class="frow">
        ${field('Email', input('email', '', 'type="email" maxlength="254"'))}
        ${field('Phone', input('phone', '', 'maxlength="40"'))}
      </div>
      <div class="frow3">
        ${field('Address', input('addressLine1', '', 'maxlength="200"'))}
        ${field('City', input('city', '', 'maxlength="120"'))}
        ${field('Post code', input('postCode', '', 'maxlength="20"'))}
      </div>
      ${callout('info', 'i', html`
        Only what is filled in here is changed — anything left blank is left alone. The change is
        recorded in this organisation's own audit log, marked as made from the platform console.
      `)}
    `,
    collect: (root) => {
      const patch: Record<string, unknown> = {};
      for (const name of ['name', 'legalName', 'approvalReference', 'competentAuthority', 'countryCode',
        'email', 'phone', 'addressLine1', 'city', 'postCode']) {
        const v = val(root, name);
        if (v) patch[name] = name === 'countryCode' ? v.toUpperCase() : v;
      }
      if (!Object.keys(patch).length) return { error: 'Nothing was filled in.' };
      return patch;
    },
    submit: (patch) => api.console.update(id, patch),
    done: () => 'Saved',
    onDone: reload,
  });
}
